import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type SwitchVariant = 'pill' | 'flat';

/**
 * Switch allows users to toggle between two states:
 * - ON: value is true (checked)
 * - OFF: value is false (unchecked)
 *
 * Use `checked` for controlled state and `onChange` to get the new boolean (e.target.checked).
 */
interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  labelPosition?: 'left' | 'right';
  size?: 'sm' | 'default' | 'lg';
  variant?: SwitchVariant;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, label, description, labelPosition = 'right', size = 'default', variant = 'pill', ...props }, ref) => {
    const { colors } = useTheme();

    const sizeClasses = {
      pill: { sm: 'h-4 w-7', default: 'h-5 w-9', lg: 'h-6 w-11' },
      flat: { sm: 'h-3.5 w-6', default: 'h-4 w-8', lg: 'h-5 w-10' },
    };

    const dotSizeClasses = {
      pill: { sm: 'h-3 w-3', default: 'h-4 w-4', lg: 'h-5 w-5' },
      flat: { sm: 'h-2.5 w-2.5', default: 'h-3 w-3', lg: 'h-4 w-4' },
    };

    const translateClasses = {
      pill: { sm: 'peer-checked:translate-x-3', default: 'peer-checked:translate-x-4', lg: 'peer-checked:translate-x-5' },
      flat: { sm: 'peer-checked:translate-x-2.5', default: 'peer-checked:translate-x-4', lg: 'peer-checked:translate-x-5' },
    };

    const insetClasses = {
      pill: { sm: 'top-0.5 left-0.5', default: 'top-0.5 left-0.5', lg: 'top-0.5 left-0.5' },
      flat: { sm: 'top-0.5 left-0.5', default: 'top-0.5 left-0.5', lg: 'top-0.5 left-0.5' },
    };

    const isPill = variant === 'pill';

    const switchElement = (
      <label className={cn('relative inline-flex items-center cursor-pointer group', className)}>
        <input
          type="checkbox"
          ref={ref}
          className="sr-only peer"
          {...props}
        />
        {/* Track - direct sibling of input so peer-checked works on it */}
        <div
          className={cn(
            sizeClasses[variant][size],
            'relative rounded-full transition-all duration-200 ease-out shrink-0',
            isPill && 'shadow-inner',
            !isPill && 'border-2',
            !isPill && colors.border,
            !isPill && 'peer-checked:border-transparent',
            colors.surfaceHover,
            colors.primaryChecked,
            colors.primaryCheckedHover,
            'peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2',
            colors.primaryBorderAll ? `peer-focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
            'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed'
          )}
        />
        {/* Knob - must be direct sibling of input so peer-checked:translate-x works */}
        <div
          className={cn(
            'absolute rounded-full transition-all duration-200 ease-out pointer-events-none',
            insetClasses[variant][size],
            isPill && 'shadow-md peer-checked:shadow-lg group-hover:scale-110',
            !isPill && 'shadow-sm border opacity-80',
            colors.surface,
            dotSizeClasses[variant][size],
            translateClasses[variant][size]
          )}
          style={{ marginLeft: 0 }}
        />
      </label>
    );

    if (label || description) {
      const textBlock = (
        <div className="flex-1 min-w-0">
          {label && (
            <span
              className={cn('text-sm font-medium cursor-pointer', colors.text, props.disabled && 'opacity-50 cursor-not-allowed')}
              onClick={(e) => {
                if (!props.disabled && ref && typeof ref !== 'function' && ref?.current) {
                  e.preventDefault();
                  ref.current.click();
                }
              }}
            >
              {label}
            </span>
          )}
          {description && (
            <p className={cn('text-xs mt-1', colors.textMuted)}>{description}</p>
          )}
        </div>
      );
      const gapClass = 'gap-3';
      return (
        <div className={cn('flex items-start', labelPosition === 'left' ? `flex-row-reverse ${gapClass}` : gapClass)}>
          {switchElement}
          {textBlock}
        </div>
      );
    }

    return switchElement;
  }
);

Switch.displayName = 'Switch';

export { Switch };

